/**
 * DPRPage — Daily Progress Report for `/dpr?projectId=…`
 *
 * Loads SOWs then opens the Safety/Productivity/… tabs immediately for the first pipeline section.
 * Settings opens full-page over the report.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';
import { fetchTeamMembersAccessForProject } from '../../redux/pihEquipmentDetails/action';
import { Button, Toggle, Spinner, Checkbox, ConfirmationModal } from '@crc-evans/ui-components';
import { DatePicker as AntDatePicker } from 'antd';
import { Settings, ChevronLeft, Clock, Shield, CheckCircle, TrendingUp, Users, Package, MessageSquare } from 'lucide-react';
import axiosConfig from '../../services/axiosConfig';
import { get } from 'lodash';
import SafetyLog from '../../components/DPR/SafetyLog';
import QualityLog from '../../components/DPR/QualityLog';
import PersonnelLog from '../../components/DPR/PersonnelLog';
import ConsumablesLog from '../../components/DPR/ConsumablesLog';
import DowntimeLog from '../../components/DPR/DowntimeLog';
import ProductivityLog from '../../components/DPR/ProductivityLog';
import { fetchDprProductivityLogWhenTabOpens } from '../../components/DPR/ProductivityMainlinePanel';
import CommentsLog from '../../components/DPR/CommentsLog';
import moment from 'moment';
import './DPRPage.css';
import DPRSettings from '../../components/DPR/DPRSettings';
import DprShiftSkippedNotice from '../../components/DPR/DprShiftSkippedNotice';
import '../../components/DPR/DprShiftSkippedNotice.css';
import { normalizeSowRecordFromApi } from '../../utils/sowApiFields';
import { getDprShiftLabel, isDprShiftSkipped } from '../../utils/dprShiftSkip';
import {
  getSectionReportHeaderSpec,
  resolveCompletedWeldsKpi,
  getSectionReportSummaryChips,
} from '../../utils/dprSectionReportHeader';

const Tabs = ({ items, activeTabId, onTabClick }) => {
  return (
    <div className="dpr-tabs">
      {items.map(item => (
        <div
          key={item.id}
          className={`dpr-tab-item ${activeTabId === item.id ? 'active' : ''}`}
          onClick={() => onTabClick(item.id)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          {item.icon}
          {item.label}
        </div>
      ))}
    </div>
  );
};

const DPRPage = (props) => {
  const location = useLocation();
  const history = useHistory();
  const [selectedProject, setSelectedProject] = useState(null);
  const [pipelineSections, setPipelineSections] = useState([]);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);

  // Report View State (declare before sowId / selectors that use selectedPipeline)
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [activeTab, setActiveTab] = useState('safety');
  /** Bumped when the Productivity tab is selected so embedded panels bust caches and refetch. */
  const [productivityTabRefreshToken, setProductivityTabRefreshToken] = useState(0);
  const [date, setDate] = useState(moment());
  const [shift, setShift] = useState('day');
  const [isLoadingDprLog, setIsLoadingDprLog] = useState(false);
  const [dprLogError, setDprLogError] = useState(null);
  const [showSettingsView, setShowSettingsView] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState('reportConfig');
  const [skipConfirmShift, setSkipConfirmShift] = useState(null); // 'day' | 'night' | null
  const [sectionPipeTally, setSectionPipeTally] = useState(null);

  /** Avoid re-fetching /sows when Redux only gives a new projectData array reference for the same project */
  const pipelinesFetchedForProjectIdRef = useRef(null);
  /** Stable dependency for DPR fetch (array identity from /sows changes every load even when SOW ids are unchanged) */
  const pipelineSectionsRef = useRef([]);
  pipelineSectionsRef.current = pipelineSections;
  const pipelineSectionIdsKey = useMemo(
    () =>
      Array.isArray(pipelineSections) && pipelineSections.length > 0
        ? [...new Set(pipelineSections.map((p) => p?.id).filter(Boolean))].sort((a, b) => a - b).join('|')
        : '',
    [pipelineSections]
  );

  const logDateKey = useMemo(() => moment(date).format('YYYY-MM-DD'), [date]);

  const dispatch = useDispatch();
  const sowId = selectedPipeline?.id;

  // Select skip states
  const skipDay = useSelector(state =>
    sowId ? (state.dprReducer?.skipStatesBySection?.[sowId]?.skipDay || false) : false
  );
  const skipNight = useSelector(state =>
    sowId ? (state.dprReducer?.skipStatesBySection?.[sowId]?.skipNight || false) : false
  );
  const skipDpr = useSelector(state =>
    sowId ? (state.dprReducer?.skipStatesBySection?.[sowId]?.skipDpr || false) : false
  );
  const isCurrentShiftSkipped = isDprShiftSkipped(skipDay, skipNight, shift);

  const handleSkipChange = (key, e) => {
    const checked = e?.target ? e.target.checked : e;
    if (sowId) {
      dispatch(updateDprSkipState(sowId, key, checked));
    }
  };

  const handleSkipDayShiftChange = (e) => {
    const checked = e?.target ? e.target.checked : e;
    if (!sowId) return;
    if (checked) {
      setSkipConfirmShift('day');
    } else {
      dispatch(updateDprSkipState(sowId, 'skipDay', false));
    }
  };

  const handleSkipNightShiftChange = (e) => {
    const checked = e?.target ? e.target.checked : e;
    if (!sowId) return;
    if (checked) {
      setSkipConfirmShift('night');
    } else {
      dispatch(updateDprSkipState(sowId, 'skipNight', false));
    }
  };

  const handleConfirmSkipShift = () => {
    if (sowId && skipConfirmShift) {
      dispatch(updateDprSkipState(sowId, skipConfirmShift === 'day' ? 'skipDay' : 'skipNight', true));
    }
    setSkipConfirmShift(null);
  };

  const tabItems = [
    { id: 'safety', label: 'Safety', icon: <Shield size={18} /> },
    { id: 'productivity', label: 'Productivity', icon: <TrendingUp size={18} /> },
    { id: 'quality', label: 'Quality', icon: <CheckCircle size={18} /> },
    { id: 'downtime', label: 'Downtime', icon: <Clock size={18} /> },
    { id: 'personnel', label: 'Personnel', icon: <Users size={18} /> },
    { id: 'consumables', label: 'Consumables', icon: <Package size={18} /> },
    { id: 'comments', label: 'Comments', icon: <MessageSquare size={18} /> },
  ];

  const shiftOptions = [
    {
      id: 'night',
      label: skipNight ? 'Night (Skipped)' : 'Night',
    },
    {
      id: 'day',
      label: skipDay ? 'Day (Skipped)' : 'Day',
    },
  ];

  // Get project data from URL params, Redux, or localStorage
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const projectId = params.get('projectId');

    // If projectId is in URL, find it from projectData
    if (projectId && props.projectData && Array.isArray(props.projectData)) {
      const project = props.projectData.find((p) => p.id === parseInt(projectId, 10));
      if (project) {
        setSelectedProject(project);
        localStorage.setItem('selectedProject', JSON.stringify(project));
        if (pipelinesFetchedForProjectIdRef.current !== project.id) {
          pipelinesFetchedForProjectIdRef.current = project.id;
          fetchPipelineSections(project.id);
        }
        return;
      }
    }

    // Fallback: Get from localStorage
    const storedProject = localStorage.getItem('selectedProject');
    if (storedProject) {
      try {
        const parsed = JSON.parse(storedProject);
        setSelectedProject(parsed);
        if (parsed.id && pipelinesFetchedForProjectIdRef.current !== parsed.id) {
          pipelinesFetchedForProjectIdRef.current = parsed.id;
          fetchPipelineSections(parsed.id);
        }
      } catch (e) {
        console.error('Error parsing selectedProject from localStorage:', e);
      }
    }
  }, [location.search, props.projectData]);

  // Map GET /dpr-log response to commentsData shape for CommentsLog (same as old DPR.js)
  const getCommentsDataFromDprLog = (dprLogData) => {
    if (!dprLogData || typeof dprLogData !== 'object') return {};
    const rd = dprLogData;
    const s3Url = (obj) =>
      (obj && (obj.objectUrl || obj.fileUrl || obj.url || obj.signedUrl)) || '';
    const hasApproverS3 =
      Boolean(s3Url(rd.approverSignatureS3Object)) || Boolean(s3Url(rd.approvedBy3SignatureS3Object));
    const hasApprovalData =
      (rd.approvedBy1 && (rd.approvedBy1.teamMemberName || rd.approvedBy1.signature)) ||
      (rd.approvedBy2 && (rd.approvedBy2.teamMemberName || rd.approvedBy2.signature)) ||
      (rd.approvedBy3 && (rd.approvedBy3.teamMemberName || rd.approvedBy3.signature)) ||
      Boolean(s3Url(rd.managerSignatureS3Object)) ||
      Boolean(s3Url(rd.clientSignatureS3Object)) ||
      hasApproverS3;
    const hasComments =
      (rd.dprDayShiftComments && rd.dprDayShiftComments.trim() !== '') ||
      (rd.dprNightShiftComments && rd.dprNightShiftComments.trim() !== '');
    if (rd.status === 'submitted' || hasApprovalData || hasComments) {
      const mapped = {
        dprDayShiftComments: rd.dprDayShiftComments || '',
        dprNightShiftComments: rd.dprNightShiftComments || '',
        managerName: rd.approvedBy1?.teamMemberName || '',
        clientName: rd.approvedBy2?.teamMemberName || '',
        approverName: rd.approvedBy3?.teamMemberName || '',
        managerSignature:
          rd.approvedBy1?.signature ||
          s3Url(rd.managerSignatureS3Object) ||
          s3Url(rd.approvedBy1?.s3Object) ||
          '',
        clientSignature:
          rd.approvedBy2?.signature ||
          s3Url(rd.clientSignatureS3Object) ||
          s3Url(rd.approvedBy2?.s3Object) ||
          '',
        approverSignature:
          rd.approvedBy3?.signature ||
          s3Url(rd.approverSignatureS3Object) ||
          s3Url(rd.approvedBy3SignatureS3Object) ||
          s3Url(rd.approvedBy3?.s3Object) ||
          (typeof rd.approverSignature === 'string' ? rd.approverSignature : '') ||
          '',
        managerSignatureTimestamp: rd.approvedBy1?.signedAt || null,
        clientSignatureTimestamp: rd.approvedBy2?.signedAt || null,
        approverSignatureTimestamp: rd.approvedBy3?.signedAt || null,
      };
      if (process.env.NODE_ENV === 'development') {
        const preview = (u) => (typeof u === 'string' && u.length > 0 ? `${u.slice(0, 72)}…` : '(empty)');
        // eslint-disable-next-line no-console
        console.info('[DPR comments map]', {
          dprLogId: rd.id,
          status: rd.status,
          hasApprovalData,
          hasComments,
          managerSig: preview(mapped.managerSignature),
          clientSig: preview(mapped.clientSignature),
          approverSig: preview(mapped.approverSignature),
        });
      }
      return mapped;
    }
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.info('[DPR comments map] empty — no submitted status, approval block, or shift comments', {
        dprLogId: rd.id,
        status: rd.status,
      });
    }
    return {};
  };

  const fetchPipelineSections = async (projectId) => {
    setIsLoadingPipelines(true);
    try {
      const response = await axiosConfig.get(`/sows?projectId=${projectId}`);
      let sections = [];
      if (response.data && Array.isArray(response.data)) {
        sections = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        sections = response.data.data;
      }
      sections = sections.map((row) => normalizeSowRecordFromApi(row));
      setPipelineSections(sections);
      setSelectedPipeline((prev) => {
        if (!sections.length) return null;
        if (prev && sections.some((s) => String(s.id) === String(prev.id))) return prev;
        return sections[0];
      });
    } catch (error) {
      console.error('Error fetching pipeline sections:', error);
      setPipelineSections([]);
      setSelectedPipeline(null);
    } finally {
      setIsLoadingPipelines(false);
    }
  };

  /** One GET /dpr-log per project/date (no sowId) — avoids N parallel requests. */
  const fetchDprLogForProjectDate = async (projectId, logCreatedBy, logDate) => {
    const params = new URLSearchParams({
      projectId: String(projectId),
      logCreatedBy: logCreatedBy || '',
      logDate,
    });
    const response = await axiosConfig.get(`/dpr-log?${params.toString()}`);
    return response?.data?.data ?? response?.data;
  };

  /**
   * Build `sowId -> dprLog` map for Productivity multi-mainline from a single API response:
   * - Array of logs: index by each item's sowId
   * - Wrapped arrays: `dprLogs`, `logs`, nested `data`
   * - Map: `bySowId`
   * - Single log object (project-wide): replicate to each pipeline section id
   */
  const buildDprLogMapFromProjectResponse = (responseData, sections) => {
    const map = {};
    const put = (data, sowKey) => {
      if (data && data.id != null && sowKey != null) map[sowKey] = data;
    };

    if (!responseData) return map;

    if (Array.isArray(responseData)) {
      responseData.forEach((item) => {
        const sid = item?.sowId ?? item?.sow_id ?? item?.productionJointId ?? item?.sectionId;
        if (sid != null) put(item, sid);
      });
      if (Object.keys(map).length === 0 && responseData.length === 1 && responseData[0]?.id != null && Array.isArray(sections)) {
        const only = responseData[0];
        sections.forEach((p) => {
          if (p?.id != null) map[p.id] = only;
        });
      }
      return map;
    }

    if (typeof responseData === 'object') {
      const nested =
        responseData.dprLogs ??
        responseData.logs ??
        (Array.isArray(responseData.data) ? responseData.data : null);
      if (Array.isArray(nested)) {
        nested.forEach((item) => {
          const sid = item?.sowId ?? item?.sow_id ?? item?.productionJointId ?? item?.sectionId;
          if (sid != null) put(item, sid);
        });
        if (Object.keys(map).length > 0) return map;
      }
      if (responseData.bySowId && typeof responseData.bySowId === 'object') {
        Object.entries(responseData.bySowId).forEach(([k, v]) => put(v, k));
        if (Object.keys(map).length > 0) return map;
      }
      if (responseData.id != null && Array.isArray(sections)) {
        sections.forEach((p) => {
          if (p?.id != null) map[p.id] = responseData;
        });
      }
    }

    return map;
  };

  const loadDprLogForProject = useCallback(async () => {
    if (!selectedProject?.id || !selectedPipeline?.id) return;
    const sections = pipelineSectionsRef.current;
    if (!Array.isArray(sections) || sections.length === 0) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const logCreatedBy = user?.email || '';
    const logDate = logDateKey;

    const responseData = await fetchDprLogForProjectDate(
      selectedProject.id,
      logCreatedBy,
      logDate
    ).catch((err) => {
      console.error('Error fetching DPR log (project scope):', err);
      throw err;
    });

    const map = buildDprLogMapFromProjectResponse(responseData, sections);
    setSelectedPipeline((prev) => {
      if (!prev?.id) return prev;
      const updated = map[prev.id];
      if (updated) {
        return { ...prev, dprLogId: updated.id, dprLogData: updated };
      }
      return { ...prev, dprLogId: undefined, dprLogData: undefined };
    });
  }, [logDateKey, selectedProject?.id, selectedPipeline?.id, pipelineSectionIdsKey]);

  // Fetch DPR log(s) when pipeline/date or SOW list membership changes — not on every new array reference from /sows
  useEffect(() => {
    if (!selectedPipeline?.id || !selectedProject?.id) return;
    if (!pipelineSectionIdsKey) return;
    let cancelled = false;
    setIsLoadingDprLog(true);
    setDprLogError(null);
    loadDprLogForProject()
      .then(() => {
        if (cancelled) return;
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Error fetching DPR logs:', error);
          setDprLogError(error?.response?.data?.message || 'Failed to load DPR log.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDprLog(false);
      });
    return () => { cancelled = true; };
  }, [logDateKey, selectedPipeline?.id, selectedProject?.id, pipelineSectionIdsKey, loadDprLogForProject]);

  // Sync skip day/night/DPR from API when dprLogData loads so checkboxes reflect saved state
  useEffect(() => {
    const data = selectedPipeline?.dprLogData;
    if (!selectedPipeline?.id || !data) return;
    const sowId = selectedPipeline.id;
    dispatch(updateDprSkipState(sowId, 'skipDay', Boolean(data.isSkippedDayShift)));
    dispatch(updateDprSkipState(sowId, 'skipNight', Boolean(data.isSkippedNightShift)));
    dispatch(updateDprSkipState(sowId, 'skipDpr', Boolean(data.isSkippedDpr)));
  }, [selectedPipeline?.id, selectedPipeline?.dprLogData, dispatch]);

  // Load team members access for Settings > Teams tab (write access)
  useEffect(() => {
    if (selectedProject?.id) {
      dispatch(fetchTeamMembersAccessForProject({ id: selectedProject.id }));
    }
  }, [selectedProject?.id, dispatch]);

  const sectionHeaderSpec = useMemo(
    () => getSectionReportHeaderSpec(selectedPipeline, selectedProject),
    [selectedPipeline, selectedProject]
  );
  const completedWeldsKpi = useMemo(
    () => resolveCompletedWeldsKpi(sectionHeaderSpec, sectionPipeTally),
    [sectionHeaderSpec, sectionPipeTally]
  );
  const sectionSummaryChips = useMemo(
    () => getSectionReportSummaryChips(sectionHeaderSpec, completedWeldsKpi),
    [sectionHeaderSpec, completedWeldsKpi]
  );

  useEffect(() => {
    const logId = selectedPipeline?.dprLogId ?? selectedPipeline?.dprLogData?.id;
    if (!logId) {
      setSectionPipeTally(null);
      return undefined;
    }
    let cancelled = false;
    axiosConfig
      .get('/dpr-dashboard/metrics', {
        params: { dprLogId: logId, shift: shift === 'night' ? 'night' : 'day' },
      })
      .then((response) => {
        if (cancelled) return;
        const data = response?.data?.data ?? response?.data;
        setSectionPipeTally(data?.pipeTally ?? null);
      })
      .catch(() => {
        if (!cancelled) setSectionPipeTally(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPipeline?.dprLogId, selectedPipeline?.dprLogData?.id, shift]);

  // Refetch DPR log and update selectedPipeline.dprLogData (so Comments and other sections show saved data on tab switch)
  const refetchDprLogForReportView = () => {
    loadDprLogForProject().catch((err) =>
      console.error('Error refetching DPR log after save:', err)
    );
  };

  const handleDprTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'productivity') {
      const logId = selectedPipeline?.dprLogId ?? selectedPipeline?.dprLogData?.id;
      if (logId != null && String(logId).trim() !== '') {
        void fetchDprProductivityLogWhenTabOpens(logId, shift).catch((err) => {
          console.error('GET /dpr-productivity (productivity tab):', err);
        });
      }
      setProductivityTabRefreshToken((n) => n + 1);
    }
  };

  const handleBackToProjects = () => {
    history.push('/projects');
  };

  if (!selectedProject) {
    return (
      <div className="dpr-page">
        <div className="dpr-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          <Spinner variant="inline" size="medium" />
          <span>Loading project data...</span>
        </div>
      </div>
    );
  }

  // --- SETTINGS (full page; must run before report so it can show when coming from the report header) ---
  if (showSettingsView) {
    return (
      <div className="dpr-page">
        <div className="dpr-page-top-section">
          <div className="dpr-page-heading">
            <h1 className="dpr-page-title">Settings</h1>
            <p className="dpr-page-description">
              {activeSettingsTab === 'reportConfig'
                ? 'Configure which sections appear in the DPR report.'
                : activeSettingsTab === 'defectTypes'
                  ? 'Manage defect codes and descriptions.'
                  : activeSettingsTab === 'rolesShift'
                    ? 'Define roles and their planned hours per shift.'
                    : 'Team settings and permissions.'}
            </p>
            <a
              href="#back"
              onClick={(e) => {
                e.preventDefault();
                setShowSettingsView(false);
              }}
              className="dpr-page-back-link"
              style={{ marginTop: '24px' }}
            >
              <div className="dpr-back-link">
                <ChevronLeft size={16} /> Back to report
              </div>
            </a>
          </div>
        </div>
        <DPRSettings
          activeTab={activeSettingsTab}
          setActiveTab={setActiveSettingsTab}
          projectId={selectedProject?.id}
          selectedProject={selectedProject}
        />
      </div>
    );
  }

  if (isLoadingPipelines && pipelineSections.length === 0) {
    return (
      <div className="dpr-page">
        <div className="dpr-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '48px' }}>
          <Spinner variant="inline" size="medium" />
          <span>Loading pipeline sections...</span>
        </div>
      </div>
    );
  }

  if (!isLoadingPipelines && pipelineSections.length === 0) {
    return (
      <div className="dpr-page">
        <div className="dpr-page-top-section">
          <div className="dpr-page-heading">
            <h1 className="dpr-page-title">Daily Progress Report</h1>
            <p className="dpr-page-description">No pipeline sections found for this project.</p>
            <a
              href="#back"
              onClick={(e) => {
                e.preventDefault();
                handleBackToProjects();
              }}
              className="dpr-page-back-link"
            >
              <div className="dpr-back-link">
                <ChevronLeft size={16} /> Back To Projects
              </div>
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedPipeline) {
    return (
      <div className="dpr-page">
        <div className="dpr-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '48px' }}>
          <Spinner variant="inline" size="medium" />
          <span>Loading report...</span>
        </div>
      </div>
    );
  }

  // --- REPORT VIEW ---
  const dprTabIds = tabItems.map((t) => t.id);
  const activeDprTabIndex = dprTabIds.indexOf(activeTab);
  const isLastDprSection =
    activeDprTabIndex >= 0 && activeDprTabIndex === dprTabIds.length - 1;
  const handleDprSectionSaveSuccess = () => {
    refetchDprLogForReportView();
    if (activeDprTabIndex >= 0 && activeDprTabIndex < dprTabIds.length - 1) {
      setActiveTab(dprTabIds[activeDprTabIndex + 1]);
    }
  };

  return (
      <div className="dpr-page">
        {/* Header Section */}
        <div className="dpr-report-header">
          <div className="dpr-report-title-row">
            {sectionHeaderSpec.projectTitle && sectionHeaderSpec.projectTitle !== 'Project' && (
              <div className="dpr-report-title-group">
                <h1 className="dpr-report-title">{sectionHeaderSpec.projectTitle}</h1>
              </div>
            )}
            {sectionSummaryChips.length > 0 && (
              <div className="dpr-section-chips-row">
                {sectionSummaryChips.map((chip) => (
                  <span
                    key={chip.key}
                    className={`dpr-section-chip${chip.highlight ? ' dpr-section-chip--kpi' : ''}`}
                  >
                    <span className="dpr-section-chip-label">{chip.label}</span>
                    <span className="dpr-section-chip-value">{chip.value}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="dpr-report-header-bottom">
            <div className="dpr-report-header-left">
              <div className="dpr-report-subtitle">
                {tabItems.find(t => t.id === activeTab)?.label} Entry
                {isCurrentShiftSkipped ? ` · ${getDprShiftLabel(shift)} shift skipped` : ''}
              </div>
              <div
                className="dpr-back-link dpr-back-link--inline"
                onClick={handleBackToProjects}
              >
                <ChevronLeft size={16} /> Back To Projects
              </div>
            </div>
            <div className="dpr-report-header-right">
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Button
                label="Settings"
                variant="tertiary"
                size="small"
                icon={Settings}
                onClick={() => setShowSettingsView(true)}
              />
              <div style={{ display: 'flex', gap: '15px', marginRight: '10px' }}>
                <Checkbox
                  label="Skip Night Shift"
                  checked={skipNight}
                  onChange={handleSkipNightShiftChange}
                  style={{ color: 'white' }}
                />
                <Checkbox
                  label="Skip Day Shift"
                  checked={skipDay}
                  onChange={handleSkipDayShiftChange}
                  style={{ color: 'white' }}
                />
              </div>
              <div style={{ width: '150px' }}>
                <AntDatePicker
                  value={date}
                  onChange={setDate}
                  format="DD-MMM-YYYY"
                  allowClear={false}
                  disabledDate={(current) => current && current > moment().endOf('day')}
                  className="dpr-date-picker"
                  dropdownClassName="dpr-date-picker-dropdown"
                  style={{
                    width: '100%',
                    height: '36px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    backgroundColor: '#FFFFFF',
                    color: '#374151',
                    fontWeight: 500
                  }}
                />
              </div>
              <div
                className={`dpr-shift-toggle-wrap${isCurrentShiftSkipped ? ' dpr-shift-toggle-wrap--skipped' : ''}`}
              >
                <Toggle
                  options={shiftOptions}
                  activeId={shift}
                  onToggle={setShift}
                />
                {isCurrentShiftSkipped ? (
                  <span className="dpr-shift-toggle-badge">
                    {getDprShiftLabel(shift)} skipped
                  </span>
                ) : null}
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="dpr-report-tabs-container">
          <Tabs
            items={tabItems}
            activeTabId={activeTab}
            onTabClick={handleDprTabClick}
          />
        </div>

        {/* Content Area */}
        <div className="dpr-report-content">
          {isCurrentShiftSkipped ? <DprShiftSkippedNotice shift={shift} /> : null}
          <div className={isCurrentShiftSkipped ? 'dpr-report-content-shift-skipped' : undefined}>
          {dprLogError && (
            <div className="dpr-report-error" style={{ padding: '12px', marginBottom: '12px', backgroundColor: '#FEE2E2', color: '#DC2626', borderRadius: '6px' }}>
              {dprLogError}
            </div>
          )}
          {isLoadingDprLog && (
            <div className="dpr-report-loading" style={{ padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', color: '#666' }}>
              <Spinner variant="inline" size="medium" />
              <span>Loading DPR log...</span>
            </div>
          )}
          {!isLoadingDprLog && activeTab === 'safety' && (
            <SafetyLog
              dprLogId={selectedPipeline.dprLogId}
              sowId={selectedPipeline.id}
              shift={shift}
              date={date}
              isShiftSkipped={isCurrentShiftSkipped}
              onSave={handleDprSectionSaveSuccess}
              isLastDprSection={isLastDprSection}
            />
          )}
          {!isLoadingDprLog && activeTab === 'personnel' && (
            <PersonnelLog
              dprLogId={selectedPipeline.dprLogId}
              sowId={selectedPipeline.id}
              shift={shift}
              selectedProject={selectedProject}
              isShiftSkipped={isCurrentShiftSkipped}
              onSave={handleDprSectionSaveSuccess}
              isLastDprSection={isLastDprSection}
            />
          )}
          {!isLoadingDprLog && activeTab === 'consumables' && (
            <ConsumablesLog
              dprLogId={selectedPipeline.dprLogId}
              sowId={selectedPipeline.id}
              shift={shift}
              projectId={selectedProject.id}
              isShiftSkipped={isCurrentShiftSkipped}
              onSave={handleDprSectionSaveSuccess}
              isLastDprSection={isLastDprSection}
            />
          )}
          {!isLoadingDprLog && activeTab === 'downtime' && (
            <DowntimeLog
              dprLogId={selectedPipeline.dprLogId}
              sowId={selectedPipeline.id}
              shift={shift}
              isShiftSkipped={isCurrentShiftSkipped}
              onSave={handleDprSectionSaveSuccess}
              isLastDprSection={isLastDprSection}
            />
          )}
          {!isLoadingDprLog && activeTab === 'comments' && (
            <CommentsLog
              commentsData={getCommentsDataFromDprLog(selectedPipeline.dprLogData)}
              dprLogId={selectedPipeline.dprLogId}
              dprLogData={selectedPipeline.dprLogData}
              dprStatus={selectedPipeline.dprLogData?.status ?? null}
              onReportGenerated={refetchDprLogForReportView}
              sowId={selectedPipeline.id}
              shift={shift}
              date={date}
              selectedProject={selectedProject}
              logDate={date}
              skipDayShift={skipDay}
              skipNightShift={skipNight}
              skipDpr={skipDpr}
              isShiftSkipped={isCurrentShiftSkipped}
              onSave={handleDprSectionSaveSuccess}
              isLastDprSection={isLastDprSection}
            />
          )}
          {!isLoadingDprLog && activeTab === 'productivity' && (
            <ProductivityLog
              productivityData={selectedPipeline.dprLogData?.dprProductivity ?? {}}
              dprLogId={selectedPipeline.dprLogId ?? selectedPipeline.dprLogData?.id}
              sowId={selectedPipeline.id}
              shift={shift}
              selectedProject={selectedProject}
              selectedPipeline={selectedPipeline}
              projectMainlines={pipelineSections}
              isShiftSkipped={isCurrentShiftSkipped}
              onSave={handleDprSectionSaveSuccess}
              isLastDprSection={isLastDprSection}
              refreshToken={productivityTabRefreshToken}
            />
          )}
          {!isLoadingDprLog && activeTab === 'quality' && (
            <QualityLog
              dprLogId={selectedPipeline.dprLogId}
              sowId={selectedPipeline.id}
              shift={shift}
              date={date}
              selectedProject={selectedProject}
              selectedPipeline={selectedPipeline}
              isShiftSkipped={isCurrentShiftSkipped}
              onSave={handleDprSectionSaveSuccess}
              isLastDprSection={isLastDprSection}
            />
          )}
          {!isLoadingDprLog && activeTab !== 'safety' && activeTab !== 'quality' && activeTab !== 'personnel' && activeTab !== 'consumables' && activeTab !== 'downtime' && activeTab !== 'comments' && activeTab !== 'productivity' && (
            <div className="dpr-tab-placeholder">
              {tabItems.find(t => t.id === activeTab)?.label} content coming soon.
            </div>
          )}
          </div>
        </div>

        <ConfirmationModal
          title={skipConfirmShift === 'day' ? 'Skip Day Shift' : 'Skip Night Shift'}
          message={`Are you sure you want to skip the ${skipConfirmShift === 'day' ? 'day' : 'night'} shift?`}
          confirmText="Confirm"
          cancelText="Cancel"
          isOpen={!!skipConfirmShift}
          onConfirm={handleConfirmSkipShift}
          onCancel={() => setSkipConfirmShift(null)}
          size="medium"
        />
      </div>
    );
};

const mapStateToProps = (state) => ({
  projectData: get(state, 'project.projectData', []),
});

export default connect(mapStateToProps)(DPRPage);
